import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Database, Loader2, Pencil, Play, Plus, RefreshCw, Search, Settings, Trash2, XCircle } from 'lucide-react';

const emptyPeriod = { name: '', date_from: '', date_to: '' };

async function api(path, options = {}) {
  const response = await fetch(path, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Request failed with HTTP ${response.status}`);
  }
  return data;
}

function formatDate(value) {
  if (!value) return 'Not yet scanned';
  return value.replace('T', ' ').replace('Z', '');
}

function Header({ activeTab, setActiveTab }) {
  const tabs = [
    { id: 'search', label: 'Search', icon: Search },
    { id: 'scan', label: 'Scan', icon: Play },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];
  return (
    <header className="border-b border-slate-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-blue-600">DSWD Foodstamp Portal</p>
          <h1 className="text-lg font-bold tracking-tight text-slate-900">WGP Scanned Beneficiaries</h1>
        </div>
        <nav className="flex rounded-lg bg-slate-100 p-0.5">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition ${active ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>
    </header>
  );
}

function PeriodSelect({ periods, selectedPeriodId, setSelectedPeriodId, label = 'Period' }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold text-slate-600">{label}</span>
      <select className="input text-xs" value={selectedPeriodId || ''} onChange={(event) => setSelectedPeriodId(event.target.value)}>
        <option value="">Select a period</option>
        {periods.map((period) => (
          <option key={period.id} value={period.id}>
            {period.name} ({period.date_from} to {period.date_to}) - {period.record_count || 0} rows
          </option>
        ))}
      </select>
    </label>
  );
}

function MerchantCombobox({ merchants, selectedMerchant, setSelectedMerchant }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const inputRef = { current: null };
  const filtered = merchants.filter((m) => m.toLowerCase().includes(filter.toLowerCase()));
  const displayValue = selectedMerchant || 'All Merchants';

  return (
    <label className="block relative">
      <span className="mb-1.5 block text-xs font-semibold text-slate-600">Merchant</span>
      <div className="relative">
        <input
          ref={(el) => { inputRef.current = el; }}
          className="input text-xs pr-8"
          placeholder="All Merchants"
          value={open ? filter : displayValue}
          onFocus={() => { setOpen(true); setFilter(''); }}
          onChange={(e) => setFilter(e.target.value)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
        />
        {selectedMerchant && (
          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            onClick={(e) => { e.preventDefault(); setSelectedMerchant(''); setFilter(''); }}
            title="Clear"
            type="button"
          >&times;</button>
        )}
      </div>
      {open && (
        <div className="absolute z-40 mt-1 w-full max-h-60 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
          <button
            className={`w-full text-left px-3 py-2 text-xs hover:bg-blue-50 ${!selectedMerchant ? 'bg-blue-50 font-semibold text-blue-700' : 'text-slate-700'}`}
            onMouseDown={(e) => { e.preventDefault(); setSelectedMerchant(''); setOpen(false); setFilter(''); }}
          >All Merchants</button>
          {filtered.map((m) => (
            <button
              key={m}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-blue-50 ${selectedMerchant === m ? 'bg-blue-50 font-semibold text-blue-700' : 'text-slate-700'}`}
              onMouseDown={(e) => { e.preventDefault(); setSelectedMerchant(m); setOpen(false); setFilter(''); }}
            >{m}</button>
          ))}
          {filtered.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No merchants found.</div>}
        </div>
      )}
    </label>
  );
}

function SearchPage({ periods, selectedPeriodId, setSelectedPeriodId }) {
  const [query, setQuery] = useState('');
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [merchants, setMerchants] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  const selectedPeriod = useMemo(() => periods.find((period) => String(period.id) === String(selectedPeriodId)), [periods, selectedPeriodId]);

  // Fetch merchants list when period changes
  useEffect(() => {
    if (!selectedPeriodId) { setMerchants([]); return; }
    (async () => {
      try {
        const data = await api(`/api/merchants?period_id=${encodeURIComponent(selectedPeriodId)}`);
        setMerchants(data.merchants || []);
      } catch (err) { setMerchants([]); }
    })();
  }, [selectedPeriodId]);

  const fetchResults = async (p = 1) => {
    if (!selectedPeriodId || (query.trim().length < 2 && !selectedMerchant)) {
      setResults([]); setError(''); setTotal(0); setPages(0);
      return;
    }
    try {
      setLoading(true);
      setError('');
      let url = `/api/search?period_id=${encodeURIComponent(selectedPeriodId)}&page=${p}`;
      if (query.trim().length >= 2) url += `&q=${encodeURIComponent(query.trim())}`;
      if (selectedMerchant) url += `&merchant=${encodeURIComponent(selectedMerchant)}`;
      const data = await api(url);
      setResults(data.results || []);
      setTotal(data.total || 0);
      setPages(data.pages || 0);
      setPage(data.page || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    if (!selectedPeriodId) return;
    try {
      setExporting(true);
      let url = `/api/export?period_id=${encodeURIComponent(selectedPeriodId)}`;
      if (query.trim().length >= 2) url += `&q=${encodeURIComponent(query.trim())}`;
      if (selectedMerchant) url += `&merchant=${encodeURIComponent(selectedMerchant)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const a = document.createElement('a');
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10);
      a.href = URL.createObjectURL(blob);
      a.download = `scanned_beneficiaries_${dateStr}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const openHistory = async (hhid) => {
    if (!hhid || hhid === '---') { return; }
    setModalData(null);
    setModalError('');
    setModalLoading(true);
    try {
      const data = await api(`/api/beneficiary-history?hhid=${encodeURIComponent(hhid)}`);
      if (data.error) { setModalError(data.error); }
      else { setModalData(data); }
    } catch (err) { setModalError(err.message); }
    finally { setModalLoading(false); }
  };

  useEffect(() => {
    if (!selectedPeriodId || (query.trim().length < 2 && !selectedMerchant)) {
      setResults([]); setError(''); setTotal(0); setPages(0);
      return;
    }
    setPage(1);
    const handle = setTimeout(() => fetchResults(1), 250);
    return () => clearTimeout(handle);
  }, [selectedPeriodId, query, selectedMerchant]);

  return (
    <div className="space-y-4">
      <section className="card p-4">
        <div className="grid gap-3 lg:grid-cols-[minmax(200px,280px)_minmax(200px,280px)_1fr]">
          <PeriodSelect periods={periods} selectedPeriodId={selectedPeriodId} setSelectedPeriodId={setSelectedPeriodId} label="Search period" />
          <MerchantCombobox merchants={merchants} selectedMerchant={selectedMerchant} setSelectedMerchant={setSelectedMerchant} />
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-slate-600">Keyword</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                className="input pl-9 text-xs"
                placeholder="Search HHID, name, address, date..."
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
          </label>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span className="rounded bg-blue-50 px-2 py-0.5 font-medium text-blue-700">{selectedPeriod ? `${(selectedPeriod.record_count || 0).toLocaleString()} rows` : 'No period'}</span>
          <span>Last scan: {selectedPeriod ? formatDate(selectedPeriod.last_scan_at) : 'N/A'}</span>
          {loading && <span className="inline-flex items-center gap-1.5 text-blue-700"><Loader2 className="h-3 w-3 animate-spin" /> Searching...</span>}
          {error && <span className="text-red-600">{error}</span>}
        </div>
      </section>

      <section className="card overflow-hidden p-0">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div>
            <h2 className="text-sm font-bold text-slate-900">Search Results</h2>
            <p className="text-xs text-slate-500">{total > 0 ? `Showing ${(page-1)*100+1}\u2013${Math.min(page*100, total)} of ${total.toLocaleString()} results` : 'Enter a keyword or select a merchant to search.'}</p>
          </div>
          {total > 0 && (
            <button
              className="btn-secondary text-xs px-3 py-1.5"
              onClick={exportCsv}
              disabled={exporting}
              title="Export all matching rows as CSV"
            >
              {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
              Export CSV
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-xs">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-3 py-2 font-semibold text-slate-600">Date Ordered</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Merchant</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Beneficiary</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Card</th>
                <th className="px-3 py-2 font-semibold text-slate-600">HHID</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Address</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Order</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Error</th>
              </tr>
            </thead>
            <tbody>
              {results.map((row, idx) => (
                <tr key={row.id} className={`border-b border-slate-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-blue-50/40`}>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-700">{row.date_ordered || '-'}</td>
                  <td className="px-3 py-2 text-slate-700">{row.merchant_name || '-'}</td>
                  <td className="px-3 py-2 font-medium text-slate-900">{row.beneficiary_name || '-'}</td>
                  <td className="px-3 py-2 text-slate-600">{row.card_type || '-'}</td>
                  <td className="px-3 py-2 font-mono text-slate-700">{row.hhid || '-'}</td>
                  <td className="max-w-[220px] truncate px-3 py-2 text-slate-600" title={row.address || ''}>{row.address || '-'}</td>
                  <td className="px-3 py-2">
                    {row.hhid && row.hhid !== '---' ? <button className="font-semibold text-blue-600 hover:text-blue-800 hover:underline" onClick={() => openHistory(row.hhid)}>View</button> : '-'}
                  </td>
                  <td className="max-w-[140px] truncate px-3 py-2 text-red-500" title={row.error || ''}>{row.error || ''}</td>
                </tr>
              ))}
              {!results.length && (
                <tr>
                  <td className="px-3 py-10 text-center text-slate-400" colSpan="8">
                    {(query.trim().length < 2 && !selectedMerchant) ? 'Enter at least two characters or select a merchant to search.' : 'No matching rows found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-4 py-2.5">
            <span className="text-xs text-slate-500">Page {page} of {pages} ({total.toLocaleString()} total)</span>
            <div className="flex flex-wrap items-center gap-0.5">
              <button className="rounded px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40" disabled={page <= 1 || loading} onClick={() => fetchResults(page - 1)}>← Prev</button>
              {(() => {
                const btns = [];
                const maxVisible = 7;
                let start = Math.max(1, page - Math.floor(maxVisible / 2));
                let end = Math.min(pages, start + maxVisible - 1);
                if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
                if (start > 1) { btns.push(<button key={1} className="rounded px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100" onClick={() => fetchResults(1)}>1</button>); if (start > 2) btns.push(<span key="ds" className="px-1 text-xs text-slate-300">…</span>); }
                for (let i = start; i <= end; i++) { btns.push(<button key={i} className={`rounded px-2 py-1 text-xs font-medium ${i === page ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`} disabled={loading} onClick={() => fetchResults(i)}>{i}</button>); }
                if (end < pages) { if (end < pages - 1) btns.push(<span key="de" className="px-1 text-xs text-slate-300">…</span>); btns.push(<button key={pages} className="rounded px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100" onClick={() => fetchResults(pages)}>{pages}</button>); }
                return btns;
              })()}
              <button className="rounded px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40" disabled={page >= pages || loading} onClick={() => fetchResults(page + 1)}>Next →</button>
            </div>
          </div>
        )}
      </section>

      {(modalLoading || modalData || modalError) && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-8" onClick={() => { setModalData(null); setModalError(''); setModalLoading(false); }}>
          <div className="relative w-full max-w-3xl rounded-lg bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full text-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700" onClick={() => { setModalData(null); setModalError(''); setModalLoading(false); }}>&times;</button>
            <div className="p-5">
              {modalLoading && <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /><span className="ml-2 text-sm text-slate-600">Loading beneficiary history...</span></div>}
              {modalError && <div className="py-6 text-center text-sm font-medium text-red-600">{modalError}</div>}
              {modalData && (
                <div className="space-y-5">
                  <div className="border-b border-slate-100 pb-4">
                    <h2 className="text-base font-bold text-slate-900">{modalData.beneficiary?.name || 'Beneficiary'}</h2>
                    <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-slate-600">
                      {modalData.beneficiary?.household_id && <span className="rounded bg-slate-100 px-2 py-0.5"><strong>HHID:</strong> {modalData.beneficiary.household_id}</span>}
                      {modalData.beneficiary?.balance && <span className="rounded bg-green-50 px-2 py-0.5 font-medium text-green-700">&#8369;{modalData.beneficiary.balance}</span>}
                      {modalData.beneficiary?.status && <span className={`rounded px-2 py-0.5 font-medium ${modalData.beneficiary.status === 'Active' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>{modalData.beneficiary.status}</span>}
                      {modalData.beneficiary?.card_type && <span className="rounded bg-slate-100 px-2 py-0.5">{modalData.beneficiary.card_type}</span>}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-bold text-slate-800">Ledger History</h3>
                      <span className="text-xs text-slate-400">({modalData.ledger?.length || 0} transactions)</span>
                    </div>
                    {modalData.ledger?.length > 0 ? (
                      <div className="overflow-x-auto rounded border border-slate-200">
                        <table className="min-w-full text-xs">
                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-slate-600">Date</th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-600">Remarks</th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-600">Credit</th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-600">Debit</th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-600">Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {modalData.ledger.map((row, i) => (
                              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}>
                                <td className="whitespace-nowrap px-3 py-1.5 text-slate-700">{row.date}</td>
                                <td className="px-3 py-1.5 text-slate-600">{row.remarks}</td>
                                <td className="whitespace-nowrap px-3 py-1.5 text-right font-medium text-green-600">{row.credit !== '0.00' && row.credit !== '0' ? row.credit : ''}</td>
                                <td className="whitespace-nowrap px-3 py-1.5 text-right font-medium text-red-600">{row.debit !== '0.00' && row.debit !== '0' ? row.debit : ''}</td>
                                <td className="whitespace-nowrap px-3 py-1.5 text-right font-semibold text-slate-800">{row.running_balance}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : <p className="text-xs text-slate-400">No ledger transactions found.</p>}
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-bold text-slate-800">Order History</h3>
                      <span className="text-xs text-slate-400">({modalData.order_history?.length || 0} orders)</span>
                    </div>
                    {modalData.order_history?.length > 0 ? (
                      <div className="overflow-x-auto rounded border border-slate-200">
                        <table className="min-w-full text-xs">
                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-slate-600">Order Code</th>
                              <th className="px-3 py-2 text-center font-semibold text-slate-600">Qty</th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-600">Total</th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-600">Status</th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-600">Date Ordered</th>
                            </tr>
                          </thead>
                          <tbody>
                            {modalData.order_history.map((row, i) => (
                              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}>
                                <td className="whitespace-nowrap px-3 py-1.5 font-mono text-slate-700">{row.order_code}</td>
                                <td className="px-3 py-1.5 text-center text-slate-700">{row.total_quantity}</td>
                                <td className="whitespace-nowrap px-3 py-1.5 text-right text-slate-800">{row.total}</td>
                                <td className="px-3 py-1.5"><span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${row.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{row.status}</span></td>
                                <td className="whitespace-nowrap px-3 py-1.5 text-slate-700">{row.date_ordered}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : <p className="text-xs text-slate-400">No order history found.</p>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function ScanPage({ periods, selectedPeriodId, setSelectedPeriodId, reloadPeriods }) {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState('');

  const startScan = async () => {
    if (!selectedPeriodId) {
      setError('Please select a period first.');
      return;
    }
    try {
      setRunning(true);
      setError('');
      setLogs([]);
      setProgress({ percent: 0, message: 'Starting scan...' });
      const data = await api('/api/scan/start', {
        method: 'POST',
        body: JSON.stringify({ period_id: selectedPeriodId })
      });
      const source = new EventSource(`/api/scan/progress/${data.job_id}`);
      const handleEvent = (event) => {
        const payload = JSON.parse(event.data);
        setProgress(payload);
        setLogs((items) => [payload, ...items].slice(0, 300));
        if (payload.type === 'done') {
          source.close();
          setRunning(false);
          reloadPeriods();
        }
        if (payload.type === 'error') {
          source.close();
          setRunning(false);
          setError(payload.message || 'Scan failed.');
        }
      };
      source.addEventListener('progress', handleEvent);
      source.addEventListener('done', handleEvent);
      source.addEventListener('error', (event) => {
        if (event.data) {
          handleEvent(event);
          return;
        }
        source.close();
        setRunning(false);
        setError('The progress connection closed unexpectedly. The scan may still be running on the server.');
      });
    } catch (err) {
      setRunning(false);
      setError(err.message);
    }
  };

  const selectedPeriod = periods.find((period) => String(period.id) === String(selectedPeriodId));
  const percent = progress?.percent ?? 0;

  return (
    <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
      <section className="card h-fit space-y-4 p-4">
        <div>
          <h2 className="text-sm font-bold text-slate-900">Run Full Merchant Scan</h2>
          <p className="mt-0.5 text-xs text-slate-500">Deletes existing records for the selected period, then scrapes all merchants.</p>
        </div>
        <PeriodSelect periods={periods} selectedPeriodId={selectedPeriodId} setSelectedPeriodId={setSelectedPeriodId} label="Scan period" />
        {selectedPeriod && (
          <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600 space-y-0.5">
            <p><span className="font-semibold text-slate-800">Range:</span> {selectedPeriod.date_from} to {selectedPeriod.date_to}</p>
            <p><span className="font-semibold text-slate-800">Rows:</span> {selectedPeriod.record_count || 0}</p>
            <p><span className="font-semibold text-slate-800">Last scan:</span> {formatDate(selectedPeriod.last_scan_at)}</p>
          </div>
        )}
        <button className="btn-primary w-full text-xs py-2" onClick={startScan} disabled={running || !selectedPeriodId}>
          {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
          {running ? 'Scanning...' : 'Start Scan'}
        </button>
        {error && <div className="rounded-lg bg-red-50 p-2.5 text-xs font-medium text-red-700">{error}</div>}
      </section>

      <section className="card space-y-3 p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-bold text-slate-900">Live Progress</h2>
            <p className="text-xs text-slate-500">Events stream from the Flask backend.</p>
          </div>
          <button className="btn-secondary text-xs px-2.5 py-1.5" onClick={reloadPeriods} disabled={running}>
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
        <div>
          <div className="mb-1.5 flex justify-between text-xs font-medium text-slate-700">
            <span>{progress?.message || 'No scan is running.'}</span>
            <span>{percent}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-blue-600 transition-all duration-300" style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} />
          </div>
        </div>
        <div className="max-h-[480px] overflow-auto rounded-lg border border-slate-200 bg-slate-950 p-3 font-mono text-[11px] text-slate-200">
          {logs.length === 0 && <p className="text-slate-400">Scan logs will appear here.</p>}
          {logs.map((log, index) => (
            <div key={`${log.timestamp}-${index}`} className="mb-1.5 border-b border-white/5 pb-1.5 last:border-0">
              <span className="text-slate-400">[{formatDate(log.timestamp)}] {log.stage || log.type}</span>
              <span className={`ml-2 ${log.type === 'error' ? 'text-red-300' : log.type === 'done' ? 'text-green-300' : 'text-slate-100'}`}>{log.message}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function SettingsPage({ periods, reloadPeriods, selectedPeriodId, setSelectedPeriodId }) {
  const [form, setForm] = useState(emptyPeriod);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setMessage('');
      setError('');
      const method = editingId ? 'PUT' : 'POST';
      const path = editingId ? `/api/periods/${editingId}` : '/api/periods';
      const data = await api(path, { method, body: JSON.stringify(form) });
      setMessage(editingId ? 'Period updated.' : 'Period created.');
      setForm(emptyPeriod);
      setEditingId(null);
      setSelectedPeriodId(String(data.period.id));
      reloadPeriods();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const edit = (period) => {
    setEditingId(period.id);
    setForm({ name: period.name, date_from: period.date_from, date_to: period.date_to });
    setMessage('');
    setError('');
  };

  const remove = async (period) => {
    if (!window.confirm(`Delete period "${period.name}" and all scanned rows?`)) return;
    try {
      await api(`/api/periods/${period.id}`, { method: 'DELETE' });
      if (String(selectedPeriodId) === String(period.id)) setSelectedPeriodId('');
      reloadPeriods();
      setMessage('Period deleted.');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
      <section className="card h-fit p-4">
        <h2 className="text-sm font-bold text-slate-900">{editingId ? 'Edit Period' : 'Add Period'}</h2>
        <p className="mt-0.5 text-xs text-slate-500">Periods define the date range for the scraper.</p>
        <form className="mt-4 space-y-3" onSubmit={submit}>
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-slate-600">Name</span>
            <input className="input text-xs" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Example: June 2026 Cycle" />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold text-slate-600">Date From</span>
              <input type="date" className="input text-xs" value={form.date_from} onChange={(event) => setForm({ ...form, date_from: event.target.value })} />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold text-slate-600">Date To</span>
              <input type="date" className="input text-xs" value={form.date_to} onChange={(event) => setForm({ ...form, date_to: event.target.value })} />
            </label>
          </div>
          <div className="flex gap-2">
            <button className="btn-primary flex-1 text-xs py-2" type="submit" disabled={saving}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              {editingId ? 'Save' : 'Add Period'}
            </button>
            {editingId && (
              <button className="btn-secondary text-xs px-3 py-2" type="button" onClick={() => { setEditingId(null); setForm(emptyPeriod); }}>
                Cancel
              </button>
            )}
          </div>
          {message && <div className="flex items-center gap-1.5 rounded-lg bg-green-50 p-2 text-xs font-medium text-green-700"><CheckCircle2 className="h-3.5 w-3.5" /> {message}</div>}
          {error && <div className="flex items-center gap-1.5 rounded-lg bg-red-50 p-2 text-xs font-medium text-red-700"><XCircle className="h-3.5 w-3.5" /> {error}</div>}
        </form>
      </section>

      <section className="card overflow-hidden p-0">
        <div className="border-b border-slate-100 px-4 py-3">
          <h2 className="text-sm font-bold text-slate-900">Saved Periods</h2>
          <p className="text-xs text-slate-500">Manage scan ranges and stored records.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-xs">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-3 py-2 font-semibold text-slate-600">Name</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Date From</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Date To</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Rows</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Last Scan</th>
                <th className="px-3 py-2 text-right font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((period, idx) => (
                <tr key={period.id} className={`border-b border-slate-50 ${String(period.id) === String(selectedPeriodId) ? 'bg-blue-50/60' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                  <td className="px-3 py-2 font-medium text-slate-900">{period.name}</td>
                  <td className="px-3 py-2 text-slate-700">{period.date_from}</td>
                  <td className="px-3 py-2 text-slate-700">{period.date_to}</td>
                  <td className="px-3 py-2 text-slate-700">{period.record_count || 0}</td>
                  <td className="px-3 py-2 text-slate-600">{formatDate(period.last_scan_at)}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <button className="rounded p-1.5 text-blue-600 hover:bg-blue-50" onClick={() => edit(period)} title="Edit">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button className="rounded p-1.5 text-red-600 hover:bg-red-50" onClick={() => remove(period)} title="Delete">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!periods.length && (
                <tr>
                  <td className="px-3 py-8 text-center text-slate-400" colSpan="6">No periods created yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default function App() {
  const [activeTab, setActiveTab] = useState('search');
  const [periods, setPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const reloadPeriods = async () => {
    try {
      setError('');
      const data = await api('/api/periods');
      setPeriods(data.periods || []);
      if (!selectedPeriodId && data.periods?.length) {
        setSelectedPeriodId(String(data.periods[0].id));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadPeriods();
  }, []);

  return (
    <div className="min-h-screen">
      <Header activeTab={activeTab} setActiveTab={setActiveTab} />
      <main className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-xs font-semibold text-red-700">{error}</div>}
        {loading ? (
          <div className="card flex items-center justify-center gap-2 py-12 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading...
          </div>
        ) : (
          <>
            <div className="mb-4 grid gap-3 sm:grid-cols-3">
              <div className="card flex items-center gap-3 p-3">
                <div className="rounded-lg bg-blue-50 p-2 text-blue-700"><Database className="h-4 w-4" /></div>
                <div><p className="text-[10px] text-slate-500">Periods</p><p className="text-lg font-bold text-slate-900">{periods.length}</p></div>
              </div>
              <div className="card flex items-center gap-3 p-3">
                <div className="rounded-lg bg-green-50 p-2 text-green-700"><CheckCircle2 className="h-4 w-4" /></div>
                <div><p className="text-[10px] text-slate-500">Stored Rows</p><p className="text-lg font-bold text-slate-900">{periods.reduce((sum, period) => sum + Number(period.record_count || 0), 0).toLocaleString()}</p></div>
              </div>
              <div className="card flex items-center gap-3 p-3">
                <div className="rounded-lg bg-slate-100 p-2 text-slate-600"><RefreshCw className="h-4 w-4" /></div>
                <div><p className="text-[10px] text-slate-500">Selected Period</p><p className="truncate text-sm font-bold text-slate-900">{periods.find((period) => String(period.id) === String(selectedPeriodId))?.name || 'None'}</p></div>
              </div>
            </div>

            {activeTab === 'search' && <SearchPage periods={periods} selectedPeriodId={selectedPeriodId} setSelectedPeriodId={setSelectedPeriodId} />}
            {activeTab === 'scan' && <ScanPage periods={periods} selectedPeriodId={selectedPeriodId} setSelectedPeriodId={setSelectedPeriodId} reloadPeriods={reloadPeriods} />}
            {activeTab === 'settings' && <SettingsPage periods={periods} reloadPeriods={reloadPeriods} selectedPeriodId={selectedPeriodId} setSelectedPeriodId={setSelectedPeriodId} />}
          </>
        )}
      </main>
    </div>
  );
}
